import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import IconButton from 'components/common/IconButton';
import IconArrowHeadBottom from 'components/icons/IconArrowHeadBottom';

import pxToRem from 'styles/pxToRem';

import defaultTimeFormat from 'config/timeFormat';

const styles = theme => ({
  root: {
    width: 210,
    padding: '8px 0',
    position: 'absolute',
    boxSizing: 'border-box',
    borderRadius: 4,
    zIndex: 1200,
    '&:focus': {
      outline: 0,
    },
  },
  rootPlacementBottomLeft: {
    top: '100%',
    left: 0,
  },
  rootPlacementTopLeft: {
    top: -149,
    left: 0,
  },
  rootPlacementMiddleLeft: {
    left: '100%',
    top: '50%',
    marginTop: -75,
  },
  rootPlacementBottomRight: {
    top: '100%',
    right: 0,
  },
  rootPlacementTopRight: {
    top: -149,
    right: 0,
  },
  rootPlacementMiddleRight: {
    right: '100%',
    top: '50%',
    marginTop: -75,
  },
  button: {
    margin: '-6px 0',
    color: theme.palette.grey[600],
  },
  typography: {
    fontSize: pxToRem(20),
    textAlign: 'center',
  },
  typographySeparator: {
    margin: '-2px 3px 0',
  },
  gridItemPeriod: {
    marginLeft: 14,
  },
  iconArrowHead: {
    width: 12,
    height: 7.4,
  },
  iconArrowHeadTop: {
    transform: 'rotate(180deg)',
  },
});

const Datepicker = ({
  time,
  timeFormat,
  onTimeChange,
  placement,
  onBlur,
  disableTabFocus,
  classes,
  style,
}) => {
  const timeMoment = time.length > 0 && moment(time, timeFormat).isValid()
    ? moment(time, timeFormat) : moment('8:00 am', timeFormat);
  return (
    <Paper
      className={classNames(classes.root, {
        [classes.rootPlacementBottomLeft]: placement === 'bottom-left',
        [classes.rootPlacementTopLeft]: placement === 'top-left',
        [classes.rootPlacementMiddleLeft]: placement === 'middle-left',
        [classes.rootPlacementBottomRight]: placement === 'bottom-right',
        [classes.rootPlacementTopRight]: placement === 'top-right',
        [classes.rootPlacementMiddleRight]: placement === 'middle-right',
      })}
      {... style ? { style } : {}}
      tabIndex="-1"
    >
      <Grid
        className={classes.grid}
        container
        alignItems="center"
        justify="center"
      >
        <Grid item xs={3}>
          <IconButton
            className={classes.button}
            onClick={() => onTimeChange(timeMoment.clone().add(1, 'hour').format(timeFormat))}
            tooltipContent="Add Hours"
            {...disableTabFocus ? { tabIndex: -1 } : {}}
          >
            <IconArrowHeadBottom
              className={classNames(classes.iconArrowHead, classes.iconArrowHeadTop)}
            />
          </IconButton>
          <Typography
            className={classes.typography}
          >
            {timeMoment.format('hh')}
          </Typography>
          <IconButton
            className={classes.button}
            onClick={() => onTimeChange(timeMoment.clone().subtract(1, 'hour').format(timeFormat))}
            tooltipContent="Subtract Hours"
            {...disableTabFocus ? { tabIndex: -1 } : {}}
          >
            <IconArrowHeadBottom className={classes.iconArrowHead} />
          </IconButton>
        </Grid>
        <Grid item>
          <Typography
            className={classNames(classes.typography, classes.typographySeparator)}
          >
            :
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <IconButton
            className={classes.button}
            onClick={() => onTimeChange(timeMoment.clone().add(1, 'minute').format(timeFormat))}
            tooltipContent="Add Minutes"
            {...disableTabFocus ? { tabIndex: -1 } : {}}
          >
            <IconArrowHeadBottom
              className={classNames(classes.iconArrowHead, classes.iconArrowHeadTop)}
            />
          </IconButton>
          <Typography
            className={classes.typography}
          >
            {timeMoment.format('mm')}
          </Typography>
          <IconButton
            className={classes.button}
            onClick={() => onTimeChange(timeMoment.clone().subtract(1, 'minute').format(timeFormat))}
            tooltipContent="Subtract Minutes"
            {...disableTabFocus ? { tabIndex: -1 } : {}}
          >
            <IconArrowHeadBottom className={classes.iconArrowHead} />
          </IconButton>
        </Grid>
        <Grid
          className={classes.gridItemPeriod}
          item
          xs={3}
        >
          <IconButton
            className={classes.button}
            onClick={() => onTimeChange(timeMoment.clone().add(12, 'hours').format(timeFormat))}
            tooltipContent={timeMoment.format('a') === 'am' ? 'PM' : 'AM'}
            {...disableTabFocus ? {
              tabIndex: -1,
            } : {
              onBlur,
            }}
          >
            <IconArrowHeadBottom
              className={classNames(classes.iconArrowHead, classes.iconArrowHeadTop)}
            />
          </IconButton>
          <Typography
            className={classes.typography}
          >
            {timeMoment.format('a')}
          </Typography>
          <IconButton
            className={classes.button}
            onClick={() => onTimeChange(timeMoment.clone().add(12, 'hours').format(timeFormat))}
            tooltipContent={timeMoment.format('a') === 'am' ? 'PM' : 'AM'}
            {...disableTabFocus ? {
              tabIndex: -1,
            } : {
              onBlur,
            }}
          >
            <IconArrowHeadBottom className={classes.iconArrowHead} />
          </IconButton>
        </Grid>
      </Grid>
    </Paper>
  );
};

Datepicker.propTypes = {
  time: PropTypes.string,
  timeFormat: PropTypes.string,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    rootPlacementBottomLeft: PropTypes.string.isRequired,
    rootPlacementTopLeft: PropTypes.string.isRequired,
    rootPlacementMiddleLeft: PropTypes.string.isRequired,
    rootPlacementBottomRight: PropTypes.string.isRequired,
    rootPlacementTopRight: PropTypes.string.isRequired,
    rootPlacementMiddleRight: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    typography: PropTypes.string.isRequired,
    typographySeparator: PropTypes.string.isRequired,
    gridItemPeriod: PropTypes.string.isRequired,
    iconArrowHead: PropTypes.string.isRequired,
    iconArrowHeadTop: PropTypes.string.isRequired,
  }).isRequired,
  placement: PropTypes.oneOf([
    'bottom-left',
    'top-left',
    'bottom-right',
    'top-right',
    'middle-left',
    'middle-right',
  ]),
  style: PropTypes.shape({
    transform: PropTypes.string,
  }),
  onBlur: PropTypes.func,
  onTimeChange: PropTypes.func,
  disableTabFocus: PropTypes.bool,
};

Datepicker.defaultProps = {
  time: '12:00 am',
  timeFormat: defaultTimeFormat,
  style: null,
  onTimeChange: () => {},
  placement: 'bottom-left',
  onBlur: () => {},
  disableTabFocus: false,
};

export default withStyles(styles)(Datepicker);



// WEBPACK FOOTER //
// ./src/components/fields/TimepickerField/Timepicker.js